import { useEffect, useState, type ReactNode } from "react";
import api from "@/services/api";
import { cn } from "@/utils/cn";

const blobCache = new Map<string, string>();
const pending = new Map<string, Promise<string | null>>();

function isInstagramCdnUrl(raw: string): boolean {
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== "https:") return false;
    const host = parsed.hostname.toLowerCase();
    return (
      host === "cdninstagram.com" ||
      host.endsWith(".cdninstagram.com") ||
      host === "fbcdn.net" ||
      host.endsWith(".fbcdn.net")
    );
  } catch {
    return false;
  }
}

/** Troca o link do CDN por uma rota nossa. O browser não consegue carregar scontent.cdninstagram.com (403). */
export function instagramMediaSrc(url?: string): string | undefined {
  const value = url?.trim();
  if (!value) return undefined;
  if (value.startsWith("/api/")) return value;
  if (!isInstagramCdnUrl(value)) return value;
  return `/api/marketing/ig-media?u=${encodeURIComponent(value)}`;
}

function loadBlobUrl(src: string): Promise<string | null> {
  const cached = blobCache.get(src);
  if (cached) return Promise.resolve(cached);
  const existing = pending.get(src);
  if (existing) return existing;

  const request = api
    .get<Blob>(src, { responseType: "blob" })
    .then((res) => {
      const type = String(res.headers["content-type"] ?? res.data?.type ?? "");
      if (!type.startsWith("image/")) return null;
      const objectUrl = URL.createObjectURL(res.data);
      blobCache.set(src, objectUrl);
      return objectUrl;
    })
    .catch(() => null)
    .finally(() => {
      pending.delete(src);
    });

  pending.set(src, request);
  return request;
}

function useAuthedImage(src?: string): string | undefined {
  const [url, setUrl] = useState<string | undefined>(() => (src ? blobCache.get(src) : undefined));

  useEffect(() => {
    if (!src || !src.startsWith("/api/")) {
      setUrl(undefined);
      return;
    }
    const cached = blobCache.get(src);
    if (cached) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    setUrl(undefined);
    void loadBlobUrl(src).then((next) => {
      if (!cancelled) setUrl(next ?? undefined);
    });
    return () => {
      cancelled = true;
    };
  }, [src]);

  return url;
}

export function InstagramAvatar({
  accountKey,
  alt,
  className,
}: {
  accountKey: string;
  alt: string;
  className?: string;
}) {
  const url = useAuthedImage(`/api/marketing/avatar/${encodeURIComponent(accountKey)}`);
  const initial = (alt.replace("@", "").trim().charAt(0) || "?").toUpperCase();

  if (!url) {
    return (
      <span
        className={cn(
          "inline-flex flex-shrink-0 items-center justify-center rounded-full bg-slate-200 font-semibold text-slate-500",
          className,
        )}
        aria-hidden
      >
        {initial}
      </span>
    );
  }

  return <img src={url} alt={alt} className={cn("flex-shrink-0 rounded-full object-cover", className)} />;
}

export function InstagramRemoteImage({
  url,
  alt,
  className,
  fallback,
}: {
  url?: string;
  alt: string;
  className?: string;
  fallback?: ReactNode;
}) {
  const src = instagramMediaSrc(url);
  const authed = useAuthedImage(src?.startsWith("/api/") ? src : undefined);

  if (src && !src.startsWith("/api/")) {
    return <img src={src} alt={alt} className={className} />;
  }
  if (!authed) return <>{fallback ?? null}</>;
  return <img src={authed} alt={alt} className={className} />;
}
